import React, { useEffect, useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useReveal } from '../../hooks/useReveal';
import { ArrowRight, ArrowLeft, Newspaper, Calendar } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso, lang) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(lang === 'ar' ? 'ar-SA' : 'en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch {
    return iso;
  }
};

const LatestArticles = () => {
  const { t, isRTL, language } = useLanguage();
  const [ref, visible] = useReveal();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const lang = language === 'ar' ? 'ar' : 'en';
  const Arrow = isRTL ? ArrowLeft : ArrowRight;

  useEffect(() => {
    axios.get(`${API}/api/articles`)
      .then(r => setArticles(r.data.slice(0, 3)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Hide section entirely if no articles (don't show empty placeholder)
  if (!loading && articles.length === 0) return null;

  return (
    <section id="articles" ref={ref} className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className={`flex items-end justify-between gap-6 mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="max-w-xl">
            <p className="text-xs uppercase tracking-[0.3em] text-[#00B4D8] mb-3" data-testid="articles-overline">
              {t('articles.overline')}
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0A1628] leading-tight" data-testid="articles-title">
              {t('articles.title')}
            </h2>
            <div className="w-16 h-1 bg-[#00B4D8] mt-6 rounded-full" />
            <p className="text-base text-slate-500 mt-6">{t('articles.subtitle')}</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-2 border-[#00B4D8] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="articles-grid">
            {articles.map((a, i) => {
              const title = a[`title_${lang}`] || a.title_ar || a.title_en || '';
              const excerpt = a[`excerpt_${lang}`] || a.excerpt_ar || a.excerpt_en || '';
              const href = a.url || '';
              const isExternal = /^https?:\/\//i.test(href);
              const Wrapper = href ? 'a' : 'div';
              const wrapperProps = href
                ? {
                    href,
                    target: isExternal ? '_blank' : undefined,
                    rel: isExternal ? 'noopener noreferrer' : undefined,
                  }
                : {};
              return (
                <Wrapper
                  key={a.id}
                  {...wrapperProps}
                  data-testid={`article-card-${i}`}
                  className={`group block bg-white border border-slate-100 rounded-2xl overflow-hidden ${href ? 'hover:shadow-xl hover:border-[#00B4D8]/30 hover:-translate-y-1' : ''} transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                  style={{ transitionDelay: visible ? `${120 + i * 90}ms` : '0ms' }}
                >
                  <div className="aspect-[16/10] bg-gradient-to-br from-slate-50 to-slate-100 overflow-hidden relative">
                    {a.image ? (
                      <img
                        src={a.image.startsWith('http') ? a.image : `${API}/api/files/${a.image}`}
                        alt={title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Newspaper className="w-12 h-12 text-slate-200" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="p-6 space-y-3">
                    {a.published_at && (
                      <div className="flex items-center gap-2 text-xs text-slate-400">
                        <Calendar className="w-3.5 h-3.5" />
                        <span>{formatDate(a.published_at, lang)}</span>
                      </div>
                    )}
                    <h3 className="text-lg font-bold text-[#0A1628] line-clamp-2 group-hover:text-[#00B4D8] transition-colors leading-snug">
                      {title}
                    </h3>
                    {excerpt && (
                      <p className="text-sm text-slate-500 leading-relaxed line-clamp-3">{excerpt}</p>
                    )}
                    {href && (
                      <div className="inline-flex items-center gap-2 text-sm font-semibold text-[#00B4D8] pt-2">
                        {t('articles.readMore')}
                        <Arrow className="w-4 h-4 group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-transform" />
                      </div>
                    )}
                  </div>
                </Wrapper>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default LatestArticles;
